/* global NGX, NGXD */

// Import action types.
import ActionTypes from './actionTypes.js';

// Selectors.
import Selectors from './selectors.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';
import commonEnums from '../../../common/config/enums';

// Misc dependencies.
import { createUUID } from '../../../common/utils/user';

import { AnalyticsEvents } from '../../../common/config/api';

/**
 * <pre>
 * Voting module Actions.
 *
 * Summary of responsibilities:
 *
 *  - implements Redux action creators specific to component
 *
 * FYI:
 *
 * We are using {@link https://github.com/gaearon/redux-thunk|Redux Thunk} middleware on our {@link https://github.com/reactjs/redux|Redux} store.
 * For this reason you will see that inside each action creator, we return a function, rather than an action object directly.
 * </pre>
 *
 * @see {@link https://github.com/gaearon/redux-thunk|Redux Thunk Documentation}
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 *
 * @author Tim Loram

 * @memberof module:Voting
 * @namespace
 *
 * @type {Object}
 */
const Actions = {
  /**
   * Send votes to server and update vote counts in state.
   *
   * @param item: {object} Display item to apply vote to
   * @dispatch 	VOTING, VOTE_SUCCESS, VOTE_FAILURE
   * @events		vote:displayitem, vote:success, vote:failure
   */
  vote: function (item, itemOpts, votes, votingStrategy, fraudStrategy, captchaResponse) {
    return function (dispatch) {
      // Get Event service.
      const eventService = NGXD.getServiceContainer().getService('event');

      // Trigger event.
      eventService.trigger('vote:displayitem');

      // - - - - - - - - - - - - - - - - - -
      // Dispatch action to clear messages/post vote ctas.
      dispatch({
        type: ActionTypes.HIDE_POST_VOTE_CTA,
      });

      dispatch({
        type: ActionTypes.HIDE_MESSAGE,
      });

      // Dispatch a voting send action
      dispatch({
        type: ActionTypes.VOTING,
      });

      // - - - - - - - - - - - - - - - - - -
      // Get global config service.
      const globalConfig = NGXD.getServiceContainer().getService('config');

      // - - - - - - - - - - - - - - - - - -
      // Build the endpoint url.
      const _url = globalConfig.get('endpoints.voteSecure') + '/api/interact/d/vote.json';

      // - - - - - - - - - - - - - - - - - -
      // Define params unique to this request.
      const _params = {
        entryId: item.id,
        cID:     globalConfig.get('campaign.id'),

        // Allow set-cookie in the response to set the ngx_v cookie (strictly necessary).
        withCredentials: false,
      };

      var _uid = getVoteUID();
      if (_uid && _uid !== '') {
        _params.uid = _uid;
      }

      // Get State service.
      const stateService = NGXD.getServiceContainer().getService('state');

      // - - - - - - - - - - - - - - - - - -
      // Get the users Facebook id and token if they exist.
      if (stateService.get('user.fb.id') && stateService.get('user.fb.token')) {
        _params.fbid = stateService.get('user.fb.id');
        _params.oauth_token = stateService.get('user.fb.token');
      }

      // - - - - - - - - - - - - - - - - - -
      // Add Recaptcha response param if required.
      if (captchaResponse) {
        _params['g-captcha-response'] = captchaResponse;
      }

      // - - - - - - - - - - - - - - - - - -
      // Get Ajax service.
      const ajaxService = NGXD.getServiceContainer().getService('ajax');

      // - - - - - - - - - - - - - - - - - -
      // Execute async API call to vote on item.
      ajaxService
        .get(
          _url,
          _params

          // When response received.
        )
        .then(function (response) {
          // Grab response valies.
          const _respCode = response.status;
          const _respData = response.data;

          const itemMutable = item.asMutable({ deep: true });

          // Behave according to server response code.
          switch (_respCode) {
            // Everything ok.
            case 200:
              // If successful.
              if (_respData.voted) {
                let _votes;

                // If the server returned a new vote total, use that.
                if (_respData.currentVotes) {
                  _votes = _respData.currentVotes;
                  // Otherwise, increment.
                } else {
                  _votes = votes + 1;
                }

                // Dispatch vote success action
                dispatch({
                  type:  ActionTypes.VOTE_SUCCESS,
                  item:  item,
                  votes: _votes,
                });

                // Check if strategy requires a post vote CTA.
                if (votingStrategy === Enums.STRATEGY_VOTING_CTA) {
                  Actions.showPostVoteCTA()(dispatch);
                }
                // Check if strategy requires a post to wall.
                if (votingStrategy === Enums.STRATEGY_VOTING_POSTWALL) {
                  Actions.posttowall(item, itemOpts)(dispatch);
                }

                // API event
                NGX.Analytics.trigger(AnalyticsEvents.VOTE_SUCCESS);
                // Trigger event.
                eventService.trigger('vote:success', itemMutable);

                // If unsuccessful.
              } else {
                // Dispatch vote failure action
                dispatch({
                  type: ActionTypes.VOTE_FAILURE,
                });

                // Dispatch show message action
                dispatch({
                  type:      ActionTypes.SHOW_MESSAGE,
                  message:   _respData.message,
                  messageId: _respData.messageId,
                });

                // API event
                NGX.Analytics.trigger(AnalyticsEvents.VOTE_FAILURE);
                // Trigger event.
                eventService.trigger('vote:failure', itemMutable);
              }

              // Dispatch action to hide Recaptcha.
              Actions.hideDynRecaptcha()(dispatch);

              break;

            // Suspicious.
            case 400:
              // Show dynamic recaptcha if required.
              if (
                [Enums.STRATEGY_FRAUD_DYNCAPTCHA, Enums.STRATEGY_FRAUD_DYNCAPTCHA_TURNSTILE].includes(fraudStrategy)
              ) {
                // Dispatch action to show Recaptcha.
                Actions.showDynRecaptcha()(dispatch);
              } else {
                // Dispatch vote failure action
                dispatch({
                  type:       ActionTypes.VOTE_FAILURE,
                  suspicious: response.suspicious,
                });
              }

              break;
          }

          // If error encountered.
        })
        .catch(function (error) {});
    };
  },

  /**
   * Aborts a vote.
   *
   */
  abortVote: function () {
    return function (dispatch) {
      dispatch({
        type: ActionTypes.VOTE_FAILURE,
      });
    };
  },

  /**
   * Posts a voted item to a users Facebook wall.
   *
   * @param item: {object} Display item to post to wall
   */
  posttowall: function (item, itemOpts) {
    return function (dispatch) {
      const shareData = {
        title:   item.content.heading,
        byline:  item.content.byline,
        message: item.content.content,
        picture: item.assets.thumbnail,
        link:    'ctx:smarturl',
      };

      const shareContext = itemOpts.shareContext;

      // Get Api service.
      const apiService = NGXD.getServiceContainer().getService('api');

      // Call share Api.
      apiService.execute('action', 'share:' + shareContext + ':facebook', shareData);
    };
  },

  /**
   * Displays dynamic captcha.
   *
   */
  showDynRecaptcha: function () {
    return function (dispatch) {
      dispatch({
        type: ActionTypes.SHOW_DYN_RECAPTCHA,
      });
    };
  },

  /**
   * Hides dynamic captcha.
   *
   */
  hideDynRecaptcha: function () {
    return function (dispatch) {
      dispatch({
        type: ActionTypes.HIDE_DYN_RECAPTCHA,
      });
    };
  },

  /**
   * Displays a post vote CTA.
   *
   */
  showPostVoteCTA: function () {
    return function (dispatch) {
      // Dispatch show cta action without content
      dispatch({
        type: ActionTypes.SHOW_POST_VOTE_CTA,
      });

      // Get config service.
      const configService = NGXD.getServiceContainer().getService('config');

      // - - - - - - - - - - - - - - - - - -
      // Build the endpoint url.
      const _url = Config.api.cta;

      // - - - - - - - - - - - - - - - - - -
      // Define params unique to this request.
      const _params = {
        id:      configService.get('campaign.id'),
        pageId:  configService.get('social.fb.page.id'),
        appId:   configService.get('social.fb.app.id'),
        preview: configService.get('preview.isPreview'),
      };

      // - - - - - - - - - - - - - - - - - -
      // Get Ajax service.
      const ajaxService = NGXD.getServiceContainer().getService('ajax');

      // - - - - - - - - - - - - - - - - - -
      // Execute async API call to get cta content.
      ajaxService
        .get(
          _url,
          _params

          // When response received.
        )
        .then(function (response) {
          var _respData = response.data;

          // Dispatch show cta action with content
          dispatch({
            type:       ActionTypes.SHOW_POST_VOTE_CTA,
            ctaContent: _respData,
          });

          // If error encountered.
        })
        .catch(function (error) {});
    };
  },

  /**
   * Hides a post vote CTA.
   *
   */
  hidePostVoteCTA: function () {
    return function (dispatch) {
      // Dispatch hide cta action
      dispatch({
        type: ActionTypes.HIDE_POST_VOTE_CTA,
      });
    };
  },

  /**
   * Hides a server message.
   *
   */
  hideMessage: function () {
    return function (dispatch) {
      // Dispatch hide message action
      dispatch({
        type: ActionTypes.HIDE_MESSAGE,
      });
    };
  },
};

export default Actions;

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/* Private internal functions/logic */

/**
 * Gets a UID to use for voting
 *
 * @private
 * @returns {string} 32 character random GUID, prefixed with ngxv
 */
const getVoteUID = function () {
  var guid;
  const allowAnalytics = NGX.PrivacyManager.doAllowActionType(commonEnums.PRIVACY_TYPES.ANALYTICS);

  // Get Storage service.
  const storageService = NGXD.getServiceContainer().getService('storage');

  // Get Config service.
  const configService = NGXD.getServiceContainer().getService('config');

  // 1. If UID is set in context, return that (this is the same thing as NGX.context.uid?)
  if (allowAnalytics && configService.get('user.uid')) return configService.get('user.uid');

  // 2. If there is an ngx_v cookie, return that
  if (storageService.get('ngx_v')) return storageService.get('ngx_v');

  // 3. If there is an ngx cookie & the user has consented to analytics, return that
  if (allowAnalytics && storageService.get('ngx')) return storageService.get('ngx');

  // 4. Generate guid, store it in localStorage and cookie, then return that.
  guid = 'ngxv' + createUUID().split('-').join('').slice(4);
  storageService.set('ngx_v', guid);

  return guid;
};
